import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { getMessageBus } from "@jti/fe-message-bus";
import { Button } from "../../components/Button";
import { useSessionStorage } from "../../hooks/useStorage";
import "./InfoBanner.scss";
export const InfoBanner = ({ content, bannerType = "auto", ctaLink, ctaLabel, hideCta = false, hideCloseIcon = false, showInfoBanner = true, storageKey = "isShowInfoBanner", }) => {
    const messageBus = getMessageBus();
    const bannerRef = useRef(null);
    const { getStorage: getSession, setStorage: setSession } = useSessionStorage();
    const [showBanner, setShowBanner] = useState(false);
    useEffect(() => {
        if (!showInfoBanner) {
            setShowBanner(false);
            return;
        }
        try {
            const storedValue = getSession(storageKey, showInfoBanner);
            setShowBanner(storedValue);
        }
        catch (error) {
            console.error("Error reading from sessionStorage:", error);
            setShowBanner(showInfoBanner);
        }
    }, [showInfoBanner]);
    const updateBannerHeight = useCallback(() => {
        if (!bannerRef.current)
            return;
        const height = Math.round(bannerRef.current.getBoundingClientRect().height);
        document.documentElement.style.setProperty("--info-banner-height", `${height}px`);
    }, []);
    useEffect(() => {
        if (!showBanner || !bannerRef.current)
            return;
        updateBannerHeight();
        const resizeObserver = new ResizeObserver((entries) => {
            if (entries.length > 0 && entries[0].target === bannerRef.current) {
                updateBannerHeight();
            }
        });
        resizeObserver.observe(bannerRef.current);
        return () => {
            resizeObserver.disconnect();
            document.documentElement.style.removeProperty("--info-banner-height");
        };
    }, [showBanner, updateBannerHeight]);
    useEffect(() => {
        if (showBanner) {
            document.body.classList.add("body--info-banner");
            messageBus.publish("infobanner.active", {
                payload: {
                    infoBannerHeight: Math.round(bannerRef?.current?.getBoundingClientRect()?.height ?? 0),
                },
            });
            return () => {
                document.body.classList.remove("body--info-banner");
            };
        }
    }, [showBanner]);
    const handleClose = useCallback(() => {
        setShowBanner(false);
        try {
            setSession(storageKey, false);
            messageBus.publish("infobanner.close", { payload: null });
        }
        catch (error) {
            console.error("Error writing to sessionStorage:", error);
        }
    }, [storageKey]);
    if (!showBanner)
        return null;
    const bannerClassName = classNames("info-banner", `info-banner--${bannerType}`, {
        "light-theme": bannerType !== "auto",
    });
    return (_jsx("div", { ref: bannerRef, className: bannerClassName, "data-testid": "info-banner", role: "alert", "aria-live": "polite", children: _jsxs("div", { className: "info-banner__wrapper", children: [_jsxs("div", { className: "info-banner__content", children: [_jsx("div", { className: "info-banner__message-wrapper", children: _jsx("p", { className: "info-banner__message", children: content }) }), !hideCta && (_jsx("div", { className: "info-banner__button-wrapper", children: _jsx(Button, { className: "info-banner__button-link", variant: "cta", color: "tertiary", href: ctaLink, children: _jsx("span", { className: "info-banner__button-text", children: ctaLabel }) }) }))] }), !hideCloseIcon && (_jsx("button", { className: "info-banner__icon-wrapper", "data-testid": "infoBannerIcon", onClick: handleClose, "aria-label": "Close banner", children: _jsx(UiCrossIcon, { width: "20", height: "20", className: "info-banner__icon info-banner__icon--close" }) }))] }) }));
};
//# sourceMappingURL=InfoBanner.js.map